import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { Observable, of, Subject } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';

import { ObjectString } from '@shared/utilities';
import { Endpoint, EndpointService } from '../endpoints';
import {
	AppCluster,
	IAppKey,
	IAppKeyServiceData,
	IAppKeyUpdate,
	IAppKeyWithOptionals,
	IRequiredAppVersions, IRequiredAppVersion
} from './_models';

@Injectable({
	providedIn: 'root'
})
export class AppKeyService extends OnDestroyMixin {
	private dataSubject = new Subject<ObjectString<any>>();
	public dataObs = this.dataSubject.asObservable();

	public data: IAppKeyServiceData = {
		appKeys: {},
		clusters: {
			all: []
		}
	};

	constructor(private endpointService: EndpointService, private http: HttpClient) {
		super();
	}

	clearCache() {
		this.data.appKeys = {};
		this.data.clusters = {
			all: []
		};
	}

	public create(newAppKey: IAppKeyWithOptionals): Observable<IAppKey> {
		return this.http.post<any>(this.endpointService.url(Endpoint.appkey), newAppKey).pipe(
			tap({
				next: (appKey: IAppKey) => {
					this.data.appKeys[appKey.clientId] = appKey;

					this.dataSubject.next(this.data.appKeys);
				}
			}),
			first()
		);
	}

	public update(clientId: string, data: IAppKeyUpdate): Observable<IAppKey> {
		return this.http.post<IAppKey>(`${this.endpointService.url(Endpoint.appkey)}/${clientId}`, data).pipe(
			map((appkey: IAppKey) => {
				this.data.appKeys[clientId] = appkey;
				this.dataSubject.next(this.data.appKeys);

				return appkey;
			}),
			first()
		);
	}

	public getAll(force: boolean = false): Observable<{ [clientId: string]: IAppKey }> {
		const url = `${this.endpointService.url(Endpoint.appkey)}`;

		if (this.data.appKeys && this.data.appKeys[0] && !force) {
			return of(this.data.appKeys);
		} else {
			return this.http.get<IAppKey[]>(url).pipe(
				map((result: IAppKey[]) => {
					result.forEach((a) => {
						if (a.clientId) {
							this.data.appKeys[a.clientId] = a;
						}
					});

					return this.data.appKeys || {};
				})
			);
		}
	}

	/**
	 *
	 * @param id clientId of the App key
	 */
	get(clientId: string, force = false): Observable<IAppKey> {
		const url = `${this.endpointService.url(Endpoint.appkey)}/${clientId}`;
		return new Observable<IAppKey>((observer) => {
			if (
				force ||
				!this.data.appKeys ||
				!this.data.appKeys[clientId] ||
				!this.data.appKeys[clientId].hasOwnProperty('featureMask')
			) {
				this.http.get<IAppKey>(url).subscribe(
					(appkey: IAppKey) => {
						this.data.appKeys[appkey.clientId] = appkey;
						this.dataSubject.next(this.data.appKeys);
						observer.next(appkey);
						observer.complete();
					},
					(error: any) => {
						observer.error(error);
						observer.complete();
					}
				);
			} else {
				observer.next(this.data.appKeys[clientId]);
				observer.complete();
			}
		});
	}

	/**
	 *
	 * @param ids as list of app key clientIds to collect
	 */
	getByList(ids: string[], force: boolean = true): Observable<ObjectString<IAppKey>> {
		const appkeys: any = {};
		return new Observable<ObjectString<IAppKey>>((observer) => {
			let completedCount = 0;
			ids.forEach((id) => {
				this.get(id).subscribe(
					(appkey: IAppKey) => {
						appkeys[appkey.clientId] = appkey;
						this.data.appKeys[appkey.clientId] = appkey;
						completedCount++;
						if (completedCount === ids.length) {
							observer.next(appkeys);
							observer.complete();
						}
					},
					(error: any) => {
						completedCount++;
						if (completedCount === ids.length) {
							observer.next(appkeys);
							observer.complete();
						}
					}
				);
			});
		});
	}

	public getClusters(force: boolean = false): Observable<AppCluster[]> {
		const url = `${this.endpointService.url(Endpoint.clusters)}`;

		if (!force && (this.data.clusters.all.length > 0)) {
			return of(this.data.clusters.all);
		} else {
			return this.http.get<AppCluster[]>(url).pipe(
				map((clusters: AppCluster[]) => {
					this.data.clusters.all = clusters;
					return clusters;
				})
			);
		}
	}

	public getRequiredVersions(clientId: string): Observable<IRequiredAppVersions> {
		const url = `${this.endpointService.url(Endpoint.appVersions)}/${clientId}`;

		return this.http.get<IRequiredAppVersions>(url).pipe(first());
	}

	public deleteRequiredVersion(clientId: string, platform: string, action: string): Observable<void> {
		const url = `${this.endpointService.url(Endpoint.appVersions)}/${clientId}?os=${platform}&action=${action}`;

		return this.http.delete<void>(url).pipe(first());
	}

	public editRequiredVersion(clientId: string, platform: string, version: IRequiredAppVersion): Observable<void> {
		const url = `${this.endpointService.url(Endpoint.appVersions)}/${clientId}?os=${platform}`;

		return this.http.post<void>(url, version).pipe(first());
	}
}
